import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { GameProvider } from './contexts/GameContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import BackButton from './components/BackButton';
import Home from './pages/Home';
import Quiz from './pages/Quiz';
import SpinWheel from './pages/SpinWheel';
import Profile from './pages/Profile';
import Rewards from './pages/Rewards';
import Leaderboard from './pages/Leaderboard';
import Notifications from './pages/Notifications';
import Privacy from './pages/Privacy';
import Help from './pages/Help';
import Register from './pages/Register';
import Login from './pages/Login';
import Wallet from './pages/Wallet';
import Settings from './pages/Settings';
import DailyChallenges from './pages/DailyChallenges';
import Achievements from './pages/Achievements';
import Tournaments from './pages/Tournaments';
import ForgotPassword from './pages/ForgotPassword';
import { Toaster } from 'react-hot-toast';

function AppLayout({ children }) {
  const { user } = useAuth();
  const isAuthPage = window.location.pathname === '/login' || window.location.pathname === '/register';
  const isHomePage = window.location.pathname === '/' || window.location.pathname === '/home';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f56756] to-[#402e2c] text-white">
      {!isAuthPage && <Navbar />}
      <main className="container mx-auto px-4 py-6">
        {!isHomePage && !isAuthPage && (
          <div className="mb-6">
            <BackButton />
          </div>
        )}
        {children}
      </main>
      {!isAuthPage && <Footer />}
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#b34e42',
            color: '#fff',
            border: '1px solid #402e2c',
          },
          success: {
            iconTheme: {
              primary: '#402e2c',
              secondary: '#f56756',
            },
          },
          error: {
            iconTheme: {
              primary: '#ff4444',
              secondary: '#f56756',
            },
          },
        }}
      />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/register" />;
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  return !user ? children : <Navigate to="/home" />;
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <GameProvider>
            <Routes>
              {/* Public Routes */}
              <Route path="/register" element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } />
              <Route path="/login" element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } />
              <Route path="/forgot-password" element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              } />

              {/* Protected Routes */}
              <Route path="/home" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Home />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/quiz" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Quiz />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/spin-wheel" element={
                <ProtectedRoute>
                  <AppLayout>
                    <SpinWheel />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Profile />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/rewards" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Rewards />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/leaderboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Leaderboard />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Notifications />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/privacy" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Privacy />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/help" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Help />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/wallet" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Wallet />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Settings />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/daily-challenges" element={
                <ProtectedRoute>
                  <AppLayout>
                    <DailyChallenges />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/achievements" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Achievements />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/tournaments" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Tournaments />
                  </AppLayout>
                </ProtectedRoute>
              } />

              {/* Default Route */}
              <Route path="/" element={<Navigate to="/register" />} />
            </Routes>
          </GameProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App; 